import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, FileText, Phone, AlertTriangle, Download, Map, Stethoscope, Car, Droplets, Coffee, ArrowLeft, ExternalLink, ClipboardList, Route, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${window.location.origin}/api`;

// Documents vétérinaires officiels FFE
const documentsVeterinaires = [
  {
    title: "Carte Suivi Vétérinaire Amateur & Pro",
    description: "Document obligatoire pour les épreuves amateur et pro",
    url: "https://www.ffe.com/media/7111/download"
  },
  {
    title: "Carte Suivi Vétérinaire Club",
    description: "Document obligatoire pour les épreuves club",
    url: "https://www.ffe.com/media/7112/download"
  }
];

// Numéros d'urgence
const numerosUrgence = [
  { label: "SAMU", number: "15" },
  { label: "Pompiers", number: "18" },
  { label: "Numéro d'urgence européen", number: "112" },
  { label: "Gendarmerie", number: "17" }
];

const EventOrganization = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const [activeTab, setActiveTab] = useState("horaires");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);
  
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/events/${eventId}/organization`);
        setOrganizationData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching organization:', err);
        if (err.response?.status === 404) {
          // Event not found or no organization data yet
          setOrganizationData(null);
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (eventId) {
      fetchOrganization();
    }
  }, [eventId]);
  
  // Default event data structure
  const event = organizationData?.event || {};
  const organization = {
    horaires: organizationData?.horaires || [],
    epreuves: organizationData?.epreuves || [],
    participants: organizationData?.participants || [],
    roadbooks: organizationData?.roadbooks || [],
    traces: organizationData?.traces || [],
    numerosUtiles: organizationData?.numerosUtiles || [],
    planZones: organizationData?.planZones || {}
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-lime-600 mx-auto mb-4" />
          <p className="text-zinc-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-zinc-600 mb-4">{t('common.error')}: {error}</p>
          <Link to="/evenements">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('organization.backToEvents')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white" data-testid="event-organization-page">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-lime-800 via-lime-700 to-emerald-800 text-white overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/evenements" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            {t('organization.backToEvents')}
          </Link>
          
          <div className="max-w-4xl">
            <Badge className="mb-4 bg-white/20 text-white border-0">
              {t('organization.title')}
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              {event.title || t('organization.title')}
            </h1>
            <div className="flex flex-wrap gap-4 text-white/90">
              <span className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {event.date || t('common.comingSoon')}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {event.location || "Domaine de la Briouse, Sanxay"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 mb-8 h-auto gap-1">
              <TabsTrigger value="horaires" className="flex items-center gap-1 text-xs md:text-sm py-2">
                <Clock className="h-4 w-4" />
                <span className="hidden sm:inline">{t('organization.schedule')}</span>
              </TabsTrigger>
              <TabsTrigger value="participants" className="flex items-center gap-1 text-xs md:text-sm py-2">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">{t('organization.participants')}</span>
              </TabsTrigger>
              <TabsTrigger value="parcours" className="flex items-center gap-1 text-xs md:text-sm py-2">
                <Route className="h-4 w-4" />
                <span className="hidden sm:inline">{t('organization.route')}</span>
              </TabsTrigger>
              <TabsTrigger value="veterinaire" className="flex items-center gap-1 text-xs md:text-sm py-2">
                <Stethoscope className="h-4 w-4" />
                <span className="hidden sm:inline">{t('organization.veterinary')}</span>
              </TabsTrigger>
              <TabsTrigger value="pratique" className="flex items-center gap-1 text-xs md:text-sm py-2">
                <Map className="h-4 w-4" />
                <span className="hidden sm:inline">{t('organization.practicalInfo')}</span>
              </TabsTrigger>
            </TabsList>

            {/* Horaires Tab */}
            <TabsContent value="horaires">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Programme */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-lime-600" />
                      {t('organization.program')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {organization.horaires.length > 0 ? (
                      <div className="space-y-3">
                        {organization.horaires.map((h, idx) => (
                          <div key={idx} className="flex items-start gap-4 p-3 bg-zinc-50 rounded-lg">
                            <div className="font-mono font-bold text-lime-600 min-w-[60px]">
                              {h.heure}
                            </div>
                            <div className="text-zinc-700">{h.description}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-zinc-500 text-center py-8">
                        {t('common.comingSoon')}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Épreuves */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-fuchsia-600" />
                      {t('organization.races')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {organization.epreuves.length > 0 ? (
                      <div className="space-y-4">
                        {organization.epreuves.map((e, idx) => (
                          <div key={idx} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-semibold text-zinc-800">{e.nom}</h4>
                              <Badge variant="secondary">{e.distance}</Badge>
                            </div>
                            <div className="flex gap-4 text-sm text-zinc-500">
                              <span>{t('common.level')}: {e.niveau}</span>
                              <span>{t('common.departure')}: {e.depart}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-zinc-500 text-center py-8">
                        {t('common.comingSoon')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Participants Tab */}
            <TabsContent value="participants">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    {t('organization.participantsList')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {organization.participants.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 font-semibold">Dossard</th>
                            <th className="text-left p-3 font-semibold">Cavalier</th>
                            <th className="text-left p-3 font-semibold">Cheval</th>
                            <th className="text-left p-3 font-semibold">Épreuve</th>
                          </tr>
                        </thead>
                        <tbody>
                          {organization.participants.map((p, idx) => (
                            <tr key={idx} className="border-b hover:bg-zinc-50">
                              <td className="p-3 font-mono font-bold">{p.dossard}</td>
                              <td className="p-3">{p.cavalier}</td>
                              <td className="p-3">{p.cheval}</td>
                              <td className="p-3"><Badge variant="outline">{p.epreuve}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Users className="h-16 w-16 text-zinc-300 mx-auto mb-4" />
                      <p className="text-zinc-500 mb-2">{t('common.comingSoon')}</p>
                      <p className="text-sm text-zinc-400">{t('organization.participantsList')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Parcours Tab */}
            <TabsContent value="parcours">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Road Books */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5 text-orange-600" />
                      {t('organization.roadBooks')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {organization.roadbooks.length > 0 ? (
                      <div className="space-y-3">
                        {organization.roadbooks.map((rb, idx) => (
                          <a
                            key={idx}
                            href={rb.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg hover:bg-zinc-100 transition-colors"
                          >
                            <span className="font-medium text-zinc-700">{rb.nom}</span>
                            <Download className="h-5 w-5 text-zinc-400" />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <ClipboardList className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
                        <p className="text-zinc-500">{t('common.comingSoon')}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Cartes et tracés */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Map className="h-5 w-5 text-green-600" />
                      {t('organization.mapsRoutes')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {organization.traces.length > 0 ? (
                      <div className="space-y-3">
                        {organization.traces.map((trace, idx) => (
                          <a
                            key={idx}
                            href={trace.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg hover:bg-zinc-100 transition-colors"
                          >
                            <span className="font-medium text-zinc-700">{trace.nom}</span>
                            <ExternalLink className="h-5 w-5 text-zinc-400" />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Map className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
                        <p className="text-zinc-500">{t('common.comingSoon')}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Vétérinaire Tab */}
            <TabsContent value="veterinaire">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Cartes vétérinaires */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Stethoscope className="h-5 w-5 text-red-600" />
                      {t('organization.vetCards')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-zinc-600 mb-4">
                      Documents obligatoires à imprimer et présenter lors du contrôle vétérinaire.
                    </p>
                    <div className="space-y-3">
                      {documentsVeterinaires.map((doc, idx) => (
                        <a
                          key={idx}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md hover:border-red-200 transition-all"
                        >
                          <div>
                            <h4 className="font-medium text-zinc-800">{doc.title}</h4>
                            <p className="text-sm text-zinc-500">{doc.description}</p>
                          </div>
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Infos vétérinaires */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      {t('organization.vetInfo')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <h4 className="font-semibold text-blue-800 mb-2">{t('organization.vetZone')}</h4>
                        <p className="text-blue-700 text-sm">
                          Située près de l'arrivée. Suivre le fléchage depuis l'entrée.
                        </p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                        <h4 className="font-semibold text-amber-800 mb-2">Contrôles obligatoires</h4>
                        <ul className="text-amber-700 text-sm space-y-1">
                          <li>• Contrôle initial avant départ</li>
                          <li>• Contrôle à chaque vet-gate</li>
                          <li>• Contrôle final à l'arrivée</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Infos pratiques Tab */}
            <TabsContent value="pratique">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Numéros utiles */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="h-5 w-5 text-green-600" />
                      {t('organization.usefulNumbers')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {organization.numerosUtiles.map((n, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-zinc-50 rounded-lg">
                          <span className="text-zinc-700">{n.label}</span>
                          <a href={`tel:${n.number}`} className="font-mono font-bold text-green-600 hover:underline">
                            {n.number}
                          </a>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Numéros d'urgence */}
                <Card className="border-red-200">
                  <CardHeader className="bg-red-50">
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-5 w-5" />
                      {t('organization.emergencyNumbers')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {numerosUrgence.map((n, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                          <span className="text-red-700">{n.label}</span>
                          <a href={`tel:${n.number}`} className="font-mono font-bold text-red-600 text-xl hover:underline">
                            {n.number}
                          </a>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Plan du site */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Map className="h-5 w-5 text-purple-600" />
                      {t('organization.sitePlan')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                        <Car className="h-5 w-5 text-blue-500" />
                        <div>
                          <div className="font-medium text-zinc-700">{t('organization.parking')}</div>
                          <div className="text-sm text-zinc-500">{organization.planZones.parking || t('common.comingSoon')}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                        <Droplets className="h-5 w-5 text-cyan-500" />
                        <div>
                          <div className="font-medium text-zinc-700">{t('organization.toilets')}</div>
                          <div className="text-sm text-zinc-500">{organization.planZones.wc || t('common.comingSoon')}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                        <Stethoscope className="h-5 w-5 text-red-500" />
                        <div>
                          <div className="font-medium text-zinc-700">{t('organization.vetZone')}</div>
                          <div className="text-sm text-zinc-500">{organization.planZones.veterinaire || t('common.comingSoon')}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                        <Users className="h-5 w-5 text-orange-500" />
                        <div>
                          <div className="font-medium text-zinc-700">{t('organization.groomingZone')}</div>
                          <div className="text-sm text-zinc-500">{organization.planZones.grooming || t('common.comingSoon')}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                        <Coffee className="h-5 w-5 text-amber-500" />
                        <div>
                          <div className="font-medium text-zinc-700">{t('organization.restZone')}</div>
                          <div className="text-sm text-zinc-500">{organization.planZones.repos || t('common.comingSoon')}</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default EventOrganization;
